import commonActions from '@/store/data/common/actions'
import { errorHandler, getTokenUserEmail } from '@/utils/helpers'
import { webRequestClient, builderService } from '@/services'
import { find, flatten, get, isEmpty, toNumber } from 'lodash'
import Vue from 'vue'
import { taxonomySegmentsRequestParamsConstructor } from '@/store/data/taxonomies/helpers'

const actions = {
    /**
     * Unitly retrieves the hierarchy of selected taxo
     * @param {Object} payload
     * @param {Number} payload.taxoId - Id of the taxo to which you need to retrieve the taxonomy
     * @param {Number} payload.silent - display loader if false
     * @returns {Promise}
     */
    retrieveItems({ commit, rootState }, { taxoId, silent }) {
        return new Promise((resolve, reject) => {
            if (!silent) commit('setIsLoading', true)
            return webRequestClient
                .get(...taxonomySegmentsRequestParamsConstructor(taxoId))
                .then(customData => {
                    if (get(customData, 'status') === 'KO') {
                        reject('Error while retrieving custom data')
                        errorHandler('Error while retrieving custom data', commit)
                    }

                    const itemSubType = find(rootState.data.customTaxonomies.customTaxonomies, item => toNumber(item.id) === taxoId)

                    commit('setRetrievedItems', {
                        items: customData.list,
                        id: taxoId,
                        subType: itemSubType.taxonomy_type,
                        recency: itemSubType.retention
                    })
                    commit('setIsLoading', false)
                    resolve(customData.list)

                })
                .catch(error => {
                    reject('Error while retrieving custom data')
                    errorHandler(error, commit)
                    commit('setIsLoading', false)
                })
        })
    },

    createTaxonomy({ commit, rootState }, params) {
        return webRequestClient
            .post('/audience_admin/wam_custom_taxonomies.json', {
                ...params,
                product_account_id: toNumber(rootState.account.accountId),
                email: encodeURI(getTokenUserEmail(Vue.prototype.$keycloak))
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while creating your taxonomy', commit)
                } else {
                    commit('setCustomTaxonomy', { customTaxonomy: response })
                    commit(
                        'ui/manageTaxonomy/setRetrievedItems',
                        {
                            id: response.id,
                            items: []
                        },
                        { root: true }
                    )
                    commit('notifications/setAddedNotification', { type: 'success', message: 'Taxonomy has been successfully created' }, { root: true })
                    commit('setIsLoading', false)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
                commit('setIsLoading', false)
            })
    },

    updateTaxonomy({ commit }, params) {
        const id = toNumber(params.id)
        return webRequestClient
            .put(`/audience_admin/wam_custom_taxonomies/${id}.json`, {
                ...params,
                email: encodeURI(getTokenUserEmail(Vue.prototype.$keycloak))
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while updating your taxonomy', commit)
                } else {
                    commit('setUpdatedTaxonomy', { updatedTaxonomy: response })
                    commit('notifications/setAddedNotification', { type: 'success', message: 'Taxonomy has been successfully updated' }, { root: true })
                }
                commit('setIsLoading', false)
            })
            .catch(error => {
                errorHandler(error, commit)
                commit('setIsLoading', false)
            })
    },

    /**
     * Retrieves all custom taxonomies for an account
     * @param {Object} payload
     * @param {Number} payload.accountID - Id of the account
     * @returns {Promise}
     */
    retrieveWamCustomTaxonomies({ commit, dispatch }, { accountId }) {
        let _customTaxonomies = []
        const params = {
            params: { product_account_id: toNumber(accountId) }
        }
        return webRequestClient
            .get('/audience_admin/wam_custom_taxonomies.json', params)
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while retrieving custom taxonomies', commit)
                } else {
                    _customTaxonomies = get(response, 'list', [])
                    if (!isEmpty(_customTaxonomies)) {
                        commit('setCustomTaxonomies', { taxoList: _customTaxonomies })
                        dispatch('launchMultipleHierarchiesRetrieve', {
                            list: _customTaxonomies
                        })
                    } else {
                        commit('setSegmentsCount', 0)
                    }
                }
                commit('setIsLoading', false)
            })
            .catch(error => {
                errorHandler(error, commit)
                commit('setIsLoading', false)
            })
    },

    /**
     * Launches multiple request to retrieve hierarretrieveItemschies all at once
     * @param {Array} list Array of customTaxo which contains an id
     */
    launchMultipleHierarchiesRetrieve({ rootState, commit, dispatch }, { list }) {
        commit('setSegmentsCount', null)
        const taxonomies = Object.values(list)
        const requests = taxonomies.map(customTaxo => {
            return dispatch('retrieveItems', {
                taxoId: toNumber(customTaxo.id),
                silent: true
            })
        })
        commit('setBuilderIsLoading', true)
        commit('setTaxonomiesCount', taxonomies.length)
        const frequencyOpts = rootState.data.settings.general.frequency
        if (taxonomies.length === 0) {
            commit('setSegmentsCount', 0)
        }
        Promise.all(requests).then(async (segments) => {
            const $segments = flatten(segments)
            commit('setSegmentsCount', $segments.length)
            commit('setBuilderIsLoading', true)
            await builderService.post('/custom-taxonomies/create-all', {
                segments: flatten($segments),
                taxonomies,
                frequencyOpts,
            }).then(async(response) => {
                const { data } = response
                // console.log({ data })
                const content = data.content
                const tree = content.filter((taxo) => taxo.category === 'CustomTaxonomy')

                const filtredTree = tree.filter(node => node.children.length > 0)

                const obj = {}
                if (filtredTree.length > 0) {
                    obj[tree[0].path[0]] = {
                        [tree[0].path[1]]: filtredTree
                    }
                }


                await dispatch('dispatchCollection', { content, obj })
                commit('setIsLoading', false)
                commit('setBuilderIsLoading', false)
            }).catch((error) => {
                console.error({ error })
                commit('setIsLoading', false)
                commit('setBuilderIsLoading', false)
            })
        }).catch(() => {
            commit('setIsLoading', false)
            commit('setBuilderIsLoading', false)
            commit('setSegmentsCount', 0)
        })
    },
    async dispatchCollection({commit}, { content, obj }) {
        await commit('setCollection', content)
        await commit('setHierarchyTree', obj)
        commit('setIsLoading', false)
        commit('setBuilderIsLoading', false)
    }
}

export default { ...commonActions, ...actions }
